import { Badge, Card, SectionHeader } from '@timely/ui';
import { Link } from 'react-router-dom';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { useSetupStatus, type SetupItem, type SetupState } from '../onboarding/useSetupStatus';

/**
 * The full company setup status — every area, at once, with the same four
 * words for every one of them: Configured, Needs attention, Optional, Not
 * configured. Reopenable at any time from Settings, for a company whose
 * mandatory onboarding is long finished and just wants to check what it has
 * connected.
 */
const STATE_LABEL: Record<SetupState, string> = {
    configured: 'Configured',
    needs_attention: 'Needs attention',
    optional: 'Optional',
    not_configured: 'Not configured',
};

const STATE_TONE: Record<SetupState, 'pos' | 'warn' | 'neutral'> = {
    configured: 'pos', needs_attention: 'warn', optional: 'neutral', not_configured: 'neutral',
};

export function CompanySetup() {
    const context = useActiveWorkspace();
    const { items, loading } = useSetupStatus(context);
    const required = items.filter(i => i.required);
    const optional = items.filter(i => !i.required);

    return (
        <Page>
            <SectionHeader title="Company setup" subtitle="What is configured, what needs attention, and what is still optional." />

            <h2 className="text-[13px] font-semibold text-label-3 uppercase tracking-wide mt-7 mb-2">Required to use the book</h2>
            <Card className="divide-y divide-separator overflow-hidden">
                {required.map(item => <StatusRow key={item.area} item={item} loading={loading} />)}
            </Card>

            <h2 className="text-[13px] font-semibold text-label-3 uppercase tracking-wide mt-8 mb-2">Optional</h2>
            <Card className="divide-y divide-separator overflow-hidden">
                {optional.map(item => <StatusRow key={item.area} item={item} loading={loading} />)}
            </Card>
        </Page>
    );
}

function StatusRow({ item, loading }: { item: SetupItem; loading: boolean }) {
    return (
        <div className="px-5 py-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
                <div className="text-[14px] font-semibold text-label">
                    {item.can && item.href ? (
                        <Link to={item.href} className="text-accent hover:underline">{item.label}</Link>
                    ) : item.label}
                </div>
                <div className="text-[12.5px] text-label-3 mt-0.5 leading-snug">
                    {loading ? 'Checking…' : item.detail}
                </div>
            </div>
            {!loading && <Badge tone={STATE_TONE[item.state]}>{STATE_LABEL[item.state]}</Badge>}
        </div>
    );
}
